import {
    Box,
    Card,
    CardContent,
    Checkbox,
    FormControl,
    InputLabel,
    ListItemText,
    MenuItem,
    Typography
} from "@mui/material";
import {Chart} from "react-google-charts";
import {DashboardService} from "../../services/dashboard-service";
import {useEffect} from "react";
import * as React from "react";
import {translate} from "../../translate/translate";
import Select from "@mui/material/Select";
import {CategoryChart, TotalChart} from "../../models/basic/dashboard";
import {allYearsSelected, renderYears} from "../../utils/selected-render";
import {formatNumber} from "../../utils/format-number";

const CategoryChartsComponent = () => {

    const service = new DashboardService();
    const [totalData, setTotalData] = React.useState<any>();
    const [dataSourceYears, setDataSourceYears] = React.useState<number[]>([]);
    const [selectedYear, setSelectedYear] = React.useState<number>();

    useEffect(() => {
        loadYears();
        loadCategoryChart();
    }, []);

    useEffect(() => {
        loadCategoryChart();
    }, [selectedYear]);

    const loadYears = () => {
        service.getYearsCategoryCost().then(response => {
            setDataSourceYears(response.data.years);
            selectLastYear(response.data.years);
        }).catch(() => {
            setDataSourceYears([]);
        })
    }

    const selectLastYear = (years: number[]) => {
        const lastYear = years[0]
        setSelectedYear(lastYear);
    }

    const loadCategoryChart = () => {
        service.getCategoryCharts(selectedYear).then(response => {
            const data = response.data.categories[0]?.length ? transformCategoryData(response.data) : [];
            setTotalData(data);
        });
    }

    const transformCategoryData = (data: CategoryChart) => {
        const year = data.year.toString();
        const categories = data.categories.map((category: any) => category);
        const values = data.values.map((value: any) => {
            return Number(value);
        });

        const chartData: any[] = [];

        const header: any[] = ["Ano"];
        for (let i = 0; i < categories.length; i++) {
            header.push(categories[i], { role: "annotation" }, { role: 'tooltip', type: 'string', p: { html: true } });
        }

        chartData.push(header);

        const row: any[] = [year];
        for (let i = 0; i < values.length; i++) {
            row.push(values[i]);
            row.push(values[i] > 0 ? formatNumber(values[i], true) : "");
            const html = values[i] > 0 ? `
                    <div style="text-align: center; padding: 0.5rem;">
                        <strong>${categories[i]}</strong><br/>
                        <div style="white-space: nowrap">${formatNumber(values[i], true)}</div>
                    </div>
                ` : "";
            row.push(html);
        }
        chartData.push(row);
        return chartData;
    }

    const options = {
        legend: {
            position: "bottom",
            textStyle: {
                color: "#333",
                fontSize: 13,
            },
            format: "R$ #,##0",
        },
        chartArea: {
            left: 110,
            width: '95%',
            top: 30,
        },
        colors: [
            "#8979FF",
            "#FF928A",
            "#3CC3DF",
            "#FFAE4C",
            "#537FF1",
            "#6FD195",
            "#8C63DA",
            "#2BB7DC",
            "#1F94FF",
            "#F4CF3B",
            "#F66BBA",
            "#AAE81B",
            "#5B76C6",
            "#26CBAA",
            "#FBFF1F",
            "#E855FF",
            "#FF793B",
            "#FF4128",
            "#2537A8",
            "#B09CFF",
        ],
        annotations: {
            alwaysOutside: true,
            textStyle: {
                fontSize: 13,
                color: '#000',
                auraColor: 'none',
            },
        },
        isStacked: false,
        vAxis: {
            format: "R$ #,##0",
            minorGridlines: {
                color: 'transparent',
            }
        },
        tooltip: { isHtml: true },
    };

    return (
        <Card>
            <CardContent>
                <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                    <Typography variant={"h5"}>
                        {translate.t("category_cost_by_year")}
                    </Typography>

                    <FormControl sx={{width: "300px"}}>
                        <InputLabel>
                            {translate.t("year")}
                        </InputLabel>
                        <Select
                            name="year"
                            label={translate.t("year")}
                            variant={"outlined"}
                            fullWidth
                            autoFocus={false}
                            value={selectedYear?.toString() ?? ''}
                            onChange={(event: any) => {
                                const value = Number(event.target.value)
                                setSelectedYear(Number.isFinite(value) ? value : undefined)
                            }}
                            MenuProps={{
                                PaperProps: {
                                    style: {
                                        maxHeight: '400px',
                                    },
                                },
                                disableAutoFocusItem: true,
                            }}
                        >
                            {dataSourceYears?.map((year: number) => (
                                <MenuItem key={year} value={year}>
                                    <ListItemText primary={year}/>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                <Box sx={{
                    overflowX: "auto",
                    overflowY: "hidden",
                    maxWidth: "98%",
                }}>
                    <Chart
                        chartType="ColumnChart"
                        width="100%"
                        height="350px"
                        data={totalData}
                        options={options}
                        chartLanguage="pt-BR"
                    />
                </Box>
            </CardContent>
        </Card>
    )
}

export default CategoryChartsComponent;
